import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';

const fmtDollarAxis = (v) => v >= 1000 ? `$${(v / 1000).toLocaleString()}k` : `$${v.toLocaleString()}`;
import ChartCard from '../components/ChartCard';
import api from '../utils/api';
import { formatCurrency, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import KPICard from '../components/KPICard';

export default function AdsReport() {
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [dateRange, setDateRange] = useState(getDatePreset('today'));
  const [groupBy, setGroupBy] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [hourRange, setHourRange] = useState({ start: 0, end: 23 });
  const [campaignSort, setCampaignSort] = useState({ key: 'ad_spend', dir: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.get('/api/dashboard/kpis', { params: dateRange }),
        api.get('/api/dashboard/charts', { params: { ...dateRange, groupBy } }),
        api.get('/api/dashboard/ads-campaigns', { params: dateRange })
      ];
      if (groupBy === 'hourly') {
        requests.push(api.get('/api/dashboard/ads-hourly', { params: { date: dateRange.startDate } }));
      }
      const results = await Promise.all(requests);
      setData(results[0].data.adsPerformance);
      const chartData = results[1].data;
      setCampaigns(results[2].data);
      const hourlyIdx = groupBy === 'hourly' ? 3 : -1;
      if (hourlyIdx >= 0 && results[hourlyIdx]?.data?.hourlyTrend) {
        const adsHourly = results[hourlyIdx].data.hourlyTrend;
        chartData.dailyTrend = chartData.dailyTrend.map((point, i) => ({
          ...point,
          adSpend: (point.adSpend || 0) + (adsHourly[i]?.adSpend || 0),
          adsAttributedGmv: (point.adsAttributedGmv || 0) + (adsHourly[i]?.attributedGmv || 0),
          roas: ((adsHourly[i]?.adSpend || 0) > 0)
            ? Math.round((adsHourly[i]?.attributedGmv || 0) / (adsHourly[i]?.adSpend || 0) * 100) / 100
            : point.roas,
        }));
      }
      setCharts(chartData);
    } catch (err) {
      console.error('Ads report error:', err);
    }
    setLoading(false);
  }, [dateRange, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
  const filteredTrend = useMemo(() => {
    if (!charts?.dailyTrend || groupBy !== 'hourly') return charts?.dailyTrend;
    return charts.dailyTrend.filter((_, i) => i >= hourRange.start && i <= hourRange.end);
  }, [charts?.dailyTrend, groupBy, hourRange]);

  const avgRoas = useMemo(() => {
    if (!filteredTrend?.length) return 0;
    const withSpend = filteredTrend.filter(d => d.adSpend > 0);
    if (!withSpend.length) return 0;
    const totalSpend = withSpend.reduce((s, d) => s + d.adSpend, 0);
    const totalGmv = withSpend.reduce((s, d) => s + (d.adsAttributedGmv || 0), 0);
    return totalSpend > 0 ? Math.round(totalGmv / totalSpend * 100) / 100 : 0;
  }, [filteredTrend]);

  const groupLabel = groupBy === 'hourly' ? 'Hourly' : groupBy === 'weekly' ? 'Weekly' : groupBy === 'monthly' ? 'Monthly' : 'Daily';
  const formatXLabel = (val) => {
    if (groupBy === 'hourly') return val;
    if (groupBy === 'monthly') { const [y, m] = val.split('-'); return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); }
    if (groupBy === 'weekly') { const d = new Date(val + 'T12:00:00'); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    return val;
  };

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: '20px' }}>Ads Performance Report</h2>
      <DateRangeFilter onChange={setDateRange} initial="today" />

      {loading && <div className="loading-screen"><div className="spinner" /></div>}
      {!loading && data && <>

      <div style={{ background: '#ebf8ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#2a4365' }}>
        <strong>Attribution Note:</strong> Ads GMV uses TikTok's 24-hour attribution window. Profitability estimates use overall business cost rates applied to attributed GMV.
      </div>

      {/* SPEND SECTION */}
      <div className="kpi-section">
        <div className="kpi-section-title">Spend</div>
        <div className="kpi-grid">
          <KPICard label="Ad Spend" value={formatCurrency(data.adSpend)}
            info="Total GMV Max ad spend for the period" />
          <KPICard label="Cost per Order" value={formatCurrency(data.costPerOrder)}
            info="Ad spend divided by total orders.|Not just ads-attributed orders — reflects the overall cost of running ads across all sales." />
          <KPICard label="Ads % of GMV" value={data.adsPctOfGmv + '%'}
            info="Percentage of total shop GMV attributed to ads (24-hour attribution window)" />
          <KPICard label="Customer Acquisition Cost" value={formatCurrency(data.cac)}
            info="Ad Spend divided by new customers acquired in the period.|Blended across ads and organic — not all new customers are ads-attributed."
            secondary={`${data.newCustomers} new customers`} />
        </div>
      </div>

      {/* RETURNS SECTION */}
      <div className="kpi-section">
        <div className="kpi-section-title">Returns</div>
        <div className="kpi-grid">
          <KPICard label="Attributed GMV" value={formatCurrency(data.attributedGmv)}
            info="Revenue attributed to ads using TikTok's 24-hour attribution window" />
          <KPICard label="Organic GMV" value={formatCurrency(data.organicGmv)}
            info="Total shop GMV minus ads-attributed GMV" />
          <KPICard label="ROAS" value={data.roas?.toFixed(2) + 'x'}
            info="Return on Ad Spend.|Attributed GMV ÷ Ad Spend.|Higher is better. Below 1x means you're spending more on ads than the revenue they generate." />
        </div>
      </div>

      {/* PROFITABILITY SECTION */}
      <div className="kpi-section">
        <div className="kpi-section-title">Profitability</div>
        <div className="kpi-grid">
          <KPICard label="Ads Gross Profit" value={formatCurrency(data.adsGrossProfit)}
            info="Attributed GMV minus estimated COGS, TikTok fees, and shipping costs.|Uses overall business cost rates applied to ads-attributed revenue." />
          <KPICard label="Ads Net Profit" value={
              <span style={{ color: data.adsNetProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {formatCurrency(data.adsNetProfit)}
              </span>
            }
            info="Ads Gross Profit minus Ad Spend.|Positive = your ads are profitable after all costs.|Negative = ads cost more than the profit they generate." />
          <KPICard label="Ads Profit Margin" value={
              <span style={{ color: data.adsProfitMargin >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {data.adsProfitMargin}%
              </span>
            }
            info="Ads Net Profit as a percentage of Attributed GMV.|Compare to your overall business profit margin to see if ads are more or less profitable than organic sales."
            secondary={`Overall: ${data.overallProfitMargin}%`} />
          <KPICard label="Avg Customer LTV" value={formatCurrency(data.avgLtv)}
            info="Average Lifetime Value across all customers with at least one order.|This is the total value you can expect from a customer over their lifetime." />
          <KPICard label="LTV : CAC Ratio" value={
              <span style={{ color: data.ltvCacRatio >= 3 ? 'var(--success)' : data.ltvCacRatio >= 1 ? 'var(--text)' : 'var(--danger)' }}>
                {data.ltvCacRatio}x
              </span>
            }
            info="Avg Customer LTV divided by Customer Acquisition Cost.|Above 3x is healthy — you earn 3x+ what you spend to acquire each customer.|Below 1x means you're spending more to acquire customers than they're worth." />
        </div>
      </div>

      {charts && (
        <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Group by:</span>
          {['hourly', 'daily', 'weekly', 'monthly'].map(g => (
            <button key={g} className={`date-preset ${groupBy === g ? 'active' : ''}`}
              onClick={() => setGroupBy(g)} style={{ padding: '4px 12px', fontSize: '12px' }}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
          {groupBy === 'hourly' && (
            <>
              <span style={{ fontSize: '12px', color: 'var(--text-light)', marginLeft: '8px' }}>from</span>
              <select value={hourRange.start} onChange={e => setHourRange(prev => ({ ...prev, start: parseInt(e.target.value) }))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                {HOUR_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
              </select>
              <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>to</span>
              <select value={hourRange.end} onChange={e => setHourRange(prev => ({ ...prev, end: parseInt(e.target.value) }))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                {HOUR_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
              </select>
            </>
          )}
        </div>
        <div className="charts-grid">
          <ChartCard title={`${groupLabel} Ad Spend vs Attributed GMV`}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={filteredTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtDollarAxis} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="adSpend" name="Ad Spend" stroke="#e53e3e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="adsAttributedGmv" name="Attributed GMV (24hr)" stroke="#3182ce" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={`${groupLabel} ROAS Trend`}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={filteredTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(1) + 'x'} />
                <Tooltip formatter={(v) => v?.toFixed(2) + 'x'} />
                <ReferenceArea y1={0} y2={1} fill="#fee2e2" fillOpacity={0.4} />
                <ReferenceLine y={1} stroke="#e53e3e" strokeDasharray="6 4" label={{ value: 'Break-even', position: 'right', fontSize: 11, fill: '#e53e3e' }} />
                {avgRoas > 0 && <ReferenceLine y={avgRoas} stroke="#a0aec0" strokeDasharray="4 4" label={{ value: `Avg ${avgRoas.toFixed(1)}x`, position: 'left', fontSize: 11, fill: '#718096' }} />}
                <Line type="monotone" dataKey="roas" name="ROAS" stroke="#d69e2e" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        </div>
      )}

      {campaigns && campaigns.campaigns && campaigns.campaigns.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Campaign Performance</h3>
          <div className="scroll-x" style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {[
                    { key: 'campaign_name', label: 'Campaign' },
                    { key: 'ad_spend', label: 'Ad Spend' },
                    { key: 'attributed_gmv', label: 'Attributed GMV' },
                    { key: 'roas', label: 'ROAS' },
                    { key: 'pct_of_spend', label: '% of Spend' },
                    { key: 'active_days', label: 'Active Days' },
                  ].map(col => (
                    <th key={col.key}
                      onClick={() => setCampaignSort(prev => ({ key: col.key, dir: prev.key === col.key && prev.dir === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '10px 12px', textAlign: col.key === 'campaign_name' ? 'left' : 'right', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontWeight: 700 }}>
                      {col.label} {campaignSort.key === col.key ? (campaignSort.dir === 'desc' ? '↓' : '↑') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...campaigns.campaigns].sort((a, b) => {
                  const av = a[campaignSort.key], bv = b[campaignSort.key];
                  if (typeof av === 'string') return campaignSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                  return campaignSort.dir === 'asc' ? av - bv : bv - av;
                }).map(c => (
                  <tr key={c.campaign_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{c.campaign_name}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(c.ad_spend)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(c.attributed_gmv)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: c.roas >= 3 ? 'var(--success)' : c.roas >= 1 ? 'var(--text)' : 'var(--danger)' }}>{c.roas.toFixed(2)}x</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{c.pct_of_spend}%</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{c.active_days}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                  <td style={{ padding: '10px 12px' }}>Total</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(campaigns.totals.ad_spend)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(campaigns.totals.attributed_gmv)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{campaigns.totals.roas.toFixed(2)}x</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>100%</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>—</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
